import { AnimatePresence, motion, PanInfo } from 'framer-motion'
import {
  useCallback,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react'

import { useTimer } from '../../hooks/useTimer'

import { ToastContextState, ToastItem, ToastProviderProps } from './types'
import { Toast } from './Toast'
import {
  dragAxisByDirection,
  dragConstraintsByDirection,
  dragElasticByDirection,
  shouldDismissByDirection,
  toastDirections,
} from './animations/variants'
import { twMerge } from 'tailwind-merge'

const ToastContext = createContext<ToastContextState>({} as ToastContextState)

export const ToastProvider = ({
  children,
  delay = 4000,
  dismissDragOffset = 50,
  className,
  direction: defaultDirection = 'right',
  ...props
}: ToastProviderProps): JSX.Element => {
  const [currentToast, setCurrentToast] = useState<
    (ToastItem & { id: number }) | null
  >(null)

  const direction = useMemo(
    () => currentToast?.direction ?? defaultDirection,
    [currentToast, defaultDirection],
  )

  const timer = useTimer(() => setCurrentToast(null), delay)

  const toast = useCallback(
    (item: ToastItem) => {
      setCurrentToast({ ...item, id: Date.now() * Math.random() })
      timer.start()
    },
    [currentToast, timer.start],
  )

  const clear = useCallback(() => {
    setCurrentToast(null)
    timer.clear()
  }, [timer.clear])

  const onDragEnd = useCallback(
    (evt: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (shouldDismissByDirection(direction, info, dismissDragOffset)) {
        clear()
        evt.stopPropagation()
      }
    },
    [clear, direction, dismissDragOffset],
  )

  const containerPositionClass = useMemo(() => {
    // Mirror positioning based on direction
    switch (direction) {
      case 'left':
        return 'bottom-6 left-0 right-6 md:right-auto md:pr-0 pl-6'
      case 'top':
        return 'top-6 left-1/2 -translate-x-1/2 px-6 md:px-0'
      case 'bottom':
        return 'bottom-6 left-1/2 -translate-x-1/2 px-6 md:px-0'
      case 'right':
      default:
        return 'bottom-6 right-0 left-6 md:left-auto md:pl-0 pr-6'
    }
  }, [direction])

  const context = useMemo<ToastContextState>(
    () => ({ toast, clear }),
    [toast, clear],
  )

  return (
    <ToastContext.Provider value={context}>
      <AnimatePresence>
        {currentToast && (
          <div
            className={twMerge(
              containerPositionClass,
              'fixed w-max z-[80]',
              className,
            )}
            key={currentToast.id}
          >
            <motion.div
              initial={toastDirections[direction].initial}
              animate={toastDirections[direction].animate}
              exit={toastDirections[direction].exit}
              dragConstraints={dragConstraintsByDirection[direction]}
              dragElastic={dragElasticByDirection[direction]}
              transition={{
                duration: 0.2,
              }}
              onMouseEnter={timer.pause}
              onMouseLeave={timer.resume}
              onDragEnd={onDragEnd}
              drag={dragAxisByDirection[direction]}
            >
              <Toast
                className={className}
                title={currentToast.title}
                message={currentToast.message}
                severity={currentToast.severity}
                {...(currentToast.dismissable
                  ? { dismissable: true, onDismiss: clear }
                  : { dismissable: false })}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {children}
    </ToastContext.Provider>
  )
}

export const useToasts = () => useContext(ToastContext)
